import { useAui, useAuiState } from "@assistant-ui/react";
import { useCallback, useLayoutEffect, useRef, type ChangeEvent } from "react";
import { useSelector } from "react-redux";

import { agentClient } from "../agentClient";
import type { AgentState, OutgoingFile } from "../store/agentSlice";
import { attachmentFileId, forgetUpload, threadFileAttachment } from "./attachments";

const NO_FILES: OutgoingFile[] = [];

type Composer = ReturnType<typeof useAui>["composer"];

/**
 * What this runtime asked of its composer that its state does not show yet: getState() is the last
 * rendered state, so a second run before the next render (StrictMode's double effects) would add
 * a file twice, which assistant-ui refuses by throwing.
 */
interface Pending {
  adding: Set<number>;
  removing: Set<string>;
}

/**
 * Makes the composer's finished files the draft's: adds the ones it lacks, and drops, without
 * taking anything back, the ones the draft no longer has (taken off or sent in another view) and
 * a second chip of one file (the same bytes uploaded twice: the server keeps one link). Uploads
 * still running are this view's own and stay.
 */
function mirrorFiles(composer: Composer, files: OutgoingFile[], pending: Pending) {
  const { attachments, submission } = composer.getState();
  const held = new Map(attachments.map((attachment) => [attachment.id, attachment]));
  for (const id of pending.removing) if (!held.has(id)) pending.removing.delete(id);
  // A send waiting on an upload holds the files it took; its message clears the draft.
  if (submission) return;
  const wanted = new Set(files.map((file) => file.id));
  const shown = new Set<number>();
  for (const attachment of held.values()) {
    const fileId = attachmentFileId(attachment.id);
    if (fileId == null) continue;
    pending.adding.delete(fileId);
    if (wanted.has(fileId) && !shown.has(fileId)) {
      shown.add(fileId);
    } else if (attachment.status.type !== "running" && !pending.removing.has(attachment.id)) {
      pending.removing.add(attachment.id);
      forgetUpload(attachment.id);
      void composer.attachment({ id: attachment.id }).remove();
    }
  }
  for (const file of files) {
    if (shown.has(file.id) || pending.adding.has(file.id)) continue;
    pending.adding.add(file.id);
    void composer.addAttachment(threadFileAttachment(file));
  }
}

/**
 * Keeps the composer of the runtime around it and the thread's synced draft as one: its text and
 * its finished files. Call it inside an AgentRuntime and spread the result onto
 * ComposerPrimitive.Input.
 *
 * Draft -> composer: a remote edit, a snapshot or a thread switch is written into the composer,
 * once the runtime shows `threadId` (a switch builds its new thread, with an empty composer, in
 * an effect after ours). Composer -> draft: only the input's own change events, so nothing the
 * runtime does to its composer state (resets, StrictMode re-runs) is ever taken for typing. Files
 * reach the draft through agentClient.attachFile and detachFile, never from the composer state.
 */
export function useComposerSync(threadId: number | null) {
  const aui = useAui();
  const client = useRef(aui);
  client.current = aui;
  const draft = useSelector((state: { agent: AgentState }) =>
    threadId == null ? "" : (state.agent.threads[threadId]?.composer.text ?? "")
  );
  const files = useSelector((state: { agent: AgentState }) =>
    threadId == null ? NO_FILES : (state.agent.threads[threadId]?.composer.files ?? NO_FILES)
  );
  const shown = useAuiState((state) => state.threadListItem.id);
  const ready = threadId != null && shown === String(threadId);
  // Re-run the mirror once the composer shows what it was asked (pending work settles) and when an
  // upload finishes (it may be a second chip of a file already shown).
  const held = useAuiState((state) =>
    state.composer.attachments.map((item) => `${item.id}:${item.status.type}`).join(" ")
  );
  const pending = useRef<Pending>({ adding: new Set(), removing: new Set() });

  useLayoutEffect(() => {
    if (!ready) return;
    const composer = client.current.composer;
    if (composer.getState().text !== draft) composer.setText(draft);
  }, [draft, ready, shown]);

  useLayoutEffect(() => {
    if (ready) mirrorFiles(client.current.composer, files, pending.current);
  }, [files, ready, shown, held]);

  const onChange = useCallback(
    (event: ChangeEvent<HTMLTextAreaElement>) => {
      if (threadId == null) return;
      // After the Input's own handler: it runs after ours, and a store write before it re-renders
      // the Input with the old text, which moves the caret to the end.
      const text = event.target.value;
      queueMicrotask(() => agentClient.editComposer(threadId, text));
    },
    [threadId]
  );
  const onFocus = useCallback(() => {
    if (threadId != null) agentClient.focusComposer(threadId, true);
  }, [threadId]);
  const onBlur = useCallback(() => {
    if (threadId != null) agentClient.focusComposer(threadId, false);
  }, [threadId]);
  return { onChange, onFocus, onBlur };
}
